import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminService } from '../../services/adminService';
import { ImportResult } from '../../types';

const ProductImport: React.FC = () => {
    const { user } = useAuth();
    const [file, setFile] = useState<File | null>(null);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState<ImportResult | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    if (user?.role !== 'admin') {
        return (
            <div className="min-h-screen bg-eldai-gray-light flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h1>
                    <p className="text-eldai-gray">Vous n'avez pas les permissions pour accéder à cette page.</p>
                    <Link to="/" className="btn btn-primary mt-4 inline-block">
                        Retour à l'accueil
                    </Link>
                </div>
            </div>
        );
    }

    const handleFileSelect = (selectedFile: File) => {
        if (selectedFile && selectedFile.type === 'text/csv') {
            setFile(selectedFile);
            setResult(null);
        } else {
            alert('Veuillez sélectionner un fichier CSV valide.');
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            handleFileSelect(selectedFile);
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            handleFileSelect(droppedFile);
        }
    };

    const handleImport = async () => {
        if (!file) return;

        setImporting(true);
        setResult(null);

        try {
            const response = await adminService.importProducts(file);
            setResult(response);

            if (response.success) {
                setFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        } catch (error) {
            console.error('Erreur lors de l\'import:', error);
            setResult({
                success: false,
                message: 'Erreur de connexion au serveur'
            });
        } finally {
            setImporting(false);
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="min-h-screen bg-eldai-gray-light">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center mb-4">
                        <Link
                            to="/admin"
                            className="text-eldai-blue hover:text-eldai-light-blue mr-4"
                        >
                            ← Retour au tableau de bord
                        </Link>
                    </div>
                    <h1 className="text-3xl font-bold text-eldai-blue mb-2">
                        Import de produits CSV
                    </h1>
                    <p className="text-eldai-gray">
                        Importez votre catalogue de produits à partir d'un fichier CSV.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Import Section */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-xl font-bold text-eldai-blue mb-4">
                                📥 Sélectionner le fichier CSV
                            </h2>

                            {/* File Drop Zone */}
                            <div
                                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
                                    ? 'border-eldai-yellow bg-eldai-yellow bg-opacity-10'
                                    : 'border-eldai-gray hover:border-eldai-blue'
                                    }`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                            >
                                <div className="text-6xl mb-4">📄</div>
                                <p className="text-eldai-blue font-semibold mb-2">
                                    Glissez-déposez votre fichier CSV ici
                                </p>
                                <p className="text-eldai-gray text-sm mb-4">
                                    ou cliquez pour sélectionner un fichier
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="bg-eldai-blue text-white px-6 py-2 rounded-lg font-semibold hover:bg-eldai-light-blue transition-colors"
                                >
                                    Choisir un fichier
                                </button>
                            </div>

                            {/* Selected File Info */}
                            {file && (
                                <div className="mt-6 p-4 bg-eldai-gray-light rounded-lg">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <span className="text-2xl mr-3">📄</span>
                                            <div>
                                                <p className="font-semibold text-eldai-blue">{file.name}</p>
                                                <p className="text-sm text-eldai-gray">
                                                    {formatFileSize(file.size)} • Modifié le {new Date(file.lastModified).toLocaleDateString('fr-FR')}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setFile(null)}
                                            className="text-red-600 hover:text-red-800 p-1"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Import Button */}
                            <div className="mt-6 flex justify-end space-x-4">
                                <button
                                    onClick={() => {
                                        setFile(null);
                                        setResult(null);
                                        if (fileInputRef.current) {
                                            fileInputRef.current.value = '';
                                        }
                                    }}
                                    className="px-6 py-2 border border-eldai-gray text-eldai-gray rounded-lg hover:bg-eldai-gray-light transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={handleImport}
                                    disabled={!file || importing}
                                    className="px-6 py-2 bg-eldai-yellow text-eldai-blue rounded-lg font-semibold hover:bg-eldai-blue hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {importing ? 'Import en cours...' : 'Importer les produits'}
                                </button>
                            </div>

                            {/* Import Result */}
                            {result && (
                                <div className={`mt-6 p-4 rounded-lg ${result.success
                                    ? 'bg-green-50 border border-green-200'
                                    : 'bg-red-50 border border-red-200'
                                    }`}>
                                    <div className="flex items-center mb-2">
                                        <span className="text-2xl mr-3">
                                            {result.success ? '✅' : '❌'}
                                        </span>
                                        <h3 className={`font-semibold ${result.success ? 'text-green-800' : 'text-red-800'
                                            }`}>
                                            {result.success ? 'Import réussi !' : 'Erreur d\'import'}
                                        </h3>
                                    </div>
                                    <p className={`${result.success ? 'text-green-700' : 'text-red-700'
                                        }`}>
                                        {result.message}
                                    </p>

                                    {result.details && (
                                        <div className="mt-4 space-y-2">
                                            <div className="grid grid-cols-3 gap-4 text-sm">
                                                <div>
                                                    <span className="font-semibold">Total:</span> {result.details.total_rows}
                                                </div>
                                                <div>
                                                    <span className="font-semibold text-green-600">Réussis:</span> {result.details.successful_imports}
                                                </div>
                                                <div>
                                                    <span className="font-semibold text-red-600">Échecs:</span> {result.details.failed_imports}
                                                </div>
                                            </div>

                                            {result.details.products_marked_unavailable !== undefined && (
                                                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                                    <p className="text-sm text-blue-800">
                                                        <span className="font-semibold">📋 Gestion des disponibilités:</span> {result.details.products_marked_unavailable} produits existants marqués comme indisponibles, puis les produits du CSV ont été remis en disponible.
                                                    </p>
                                                </div>
                                            )}

                                            {result.details.errors && result.details.errors.length > 0 && (
                                                <div className="mt-4">
                                                    <p className="font-semibold text-red-800 mb-2">Erreurs détaillées:</p>
                                                    <ul className="text-sm text-red-700 space-y-1">
                                                        {result.details.errors.map((error, index) => (
                                                            <li key={index}>• {error}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Instructions */}
                    <div className="space-y-6">
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h3 className="text-lg font-bold text-eldai-blue mb-4">
                                📋 Format du fichier CSV
                            </h3>
                            <div className="space-y-3 text-sm">
                                <p className="text-eldai-gray">
                                    Votre fichier CSV doit contenir les colonnes suivantes:
                                </p>
                                <ul className="space-y-1 text-eldai-gray">
                                    <li><strong>Code:</strong> Code produit unique</li>
                                    <li><strong>Désignation:</strong> Nom du produit</li>
                                    <li><strong>TVA %:</strong> Taux de TVA (ex: 5.5)</li>
                                    <li><strong>PCB:</strong> Nombre de pièces par carton</li>
                                    <li><strong>EAN:</strong> Code-barres EAN13</li>
                                    <li><strong>Poids:</strong> Poids du produit</li>
                                    <li><strong>Prix Unité:</strong> Prix unitaire</li>
                                    <li><strong>Categorie:</strong> Nom de la catégorie</li>
                                    <li><strong>Emplacement:</strong> Code emplacement</li>
                                    <li><strong>Cacherout:</strong> Certification casher</li>
                                    <li><strong>PHOTO:</strong> URL de l'image (optionnel - images S3 automatiques)</li>
                                </ul>
                                <div className="mt-4 p-3 bg-eldai-blue bg-opacity-10 rounded-lg">
                                    <p className="text-sm font-semibold text-eldai-blue mb-2">
                                        📸 Images automatiques
                                    </p>
                                    <p className="text-xs text-eldai-gray mb-2">
                                        Les images sont automatiquement récupérées depuis S3 en utilisant le code produit.
                                        URL: {process.env.REACT_APP_S3_BASE_URL || 'https://eldai-commands-images.s3.eu-north-1.amazonaws.com'}/[CODE].jpg
                                    </p>
                                    <p className="text-xs text-eldai-gray">
                                        Si aucune image S3 n'est trouvée, le système utilisera l'URL fournie dans la colonne PHOTO.
                                    </p>
                                </div>
                                <div className="mt-4 p-3 bg-eldai-yellow bg-opacity-10 rounded-lg">
                                    <p className="text-sm font-semibold text-eldai-blue mb-2">
                                        📄 Fichier exemple
                                    </p>
                                    <p className="text-xs text-eldai-gray mb-3">
                                        Téléchargez un fichier CSV d'exemple pour voir le format exact.
                                    </p>
                                    <a
                                        href="/assets/exemple-import-produits.csv"
                                        download="exemple-import-produits.csv"
                                        className="inline-block bg-eldai-blue text-white px-3 py-1 rounded text-xs font-semibold hover:bg-eldai-light-blue transition-colors"
                                    >
                                        ⬇️ Télécharger l'exemple
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h3 className="text-lg font-bold text-eldai-blue mb-4">
                                💡 Conseils d'import
                            </h3>
                            <div className="space-y-3 text-sm text-eldai-gray">
                                <p>• Assurez-vous que votre fichier est encodé en UTF-8</p>
                                <p>• Les codes produits doivent être uniques</p>
                                <p>• Les prix doivent être en format numérique (ex: 1.35)</p>
                                <p>• Les catégories seront créées automatiquement si elles n'existent pas</p>
                                <p>• Taille maximum du fichier: 10 MB</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h3 className="text-lg font-bold text-eldai-blue mb-4">
                                🔄 Gestion de la disponibilité
                            </h3>
                            <div className="space-y-3 text-sm text-eldai-gray">
                                <p>• <strong>Tous les produits existants</strong> sont d'abord marqués comme indisponibles</p>
                                <p>• <strong>Seuls les produits présents dans le CSV</strong> seront remis en disponible</p>
                                <p>• Cette approche garantit que seuls les produits actuellement en stock apparaissent comme disponibles</p>
                                <p>• Les produits absents du CSV restent en base mais deviennent invisibles pour les clients</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h3 className="text-lg font-bold text-eldai-blue mb-4">
                                🔗 Actions rapides
                            </h3>
                            <div className="space-y-3">
                                <Link
                                    to="/admin/products"
                                    className="block w-full text-center border-2 border-eldai-blue text-eldai-blue py-2 px-4 rounded-lg font-semibold hover:bg-eldai-blue hover:text-white transition-colors"
                                >
                                    Voir les produits
                                </Link>
                                <Link
                                    to="/catalog"
                                    className="block w-full text-center border-2 border-eldai-gray text-eldai-gray py-2 px-4 rounded-lg font-semibold hover:bg-eldai-gray-light transition-colors"
                                >
                                    Prévisualiser le catalogue
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductImport;
